"use client";

import clsx from "clsx";
import Link from "next/link";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useDebouncedCallback } from "use-debounce";
import { useEffect, useState } from "react";
import { message } from "antd";

export const generatePagination = (currentPage: number, totalPages: number) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, "...", totalPages - 1, totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, 2, "...", totalPages - 2, totalPages - 1, totalPages];
  }

  return [
    1,
    "...",
    currentPage - 1,
    currentPage,
    currentPage + 1,
    "...",
    totalPages,
  ];
};

export default function Pagination({ totalPages }: { totalPages: number }) {
  // NOTE: Uncomment this code in Chapter 11
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 1;
  const allPages = generatePagination(currentPage, totalPages);

  const createPageURL = (pageNumber: number | string) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", pageNumber.toString());
    return `${pathname}?${params.toString()}`;
  };

  return (
    <>
      <div className="inline-flex mt-[36px]">
        <PaginationDirection
          direction="left"
          href={createPageURL(1)}
          isDisabled={currentPage <= 1}
        >
          首页
        </PaginationDirection>
        <PaginationDirection
          direction="left"
          href={createPageURL(currentPage - 1)}
          isDisabled={currentPage <= 1}
        >
          上一页
        </PaginationDirection>

        <div className="flex -space-x-px">
          {allPages.map((page, index) => {
            let position: "first" | "last" | "single" | "middle" | undefined;

            if (index === 0) position = "first";
            if (index === allPages.length - 1) position = "last";
            if (allPages.length === 1) position = "single";
            if (page === "...") position = "middle";

            return (
              <PaginationNumber
                key={page}
                href={createPageURL(page)}
                page={page}
                position={position}
                isActive={currentPage === page}
              />
            );
          })}
        </div>

        <PaginationDirection
          direction="right"
          href={createPageURL(currentPage + 1)}
          isDisabled={currentPage >= totalPages}
        >
          下一页
        </PaginationDirection>

        <PaginationDirection
          direction="right"
          href={createPageURL(totalPages)}
          isDisabled={currentPage >= totalPages}
        >
          尾页
        </PaginationDirection>

        <JumpTo totalPages={totalPages}></JumpTo>

        <TotalPage totalPages={totalPages}></TotalPage>
      </div>
    </>
  );
}

function PaginationNumber({
  page,
  href,
  isActive,
  position,
}: {
  page: number | string;
  href: string;
  position?: "first" | "last" | "middle" | "single";
  isActive: boolean;
}) {
  const className = clsx(
    "flex h-[26px] px-[2px] m-[2px] min-w-[26px] items-center justify-center text-[#666] text-[14px] border border-solid border-[#d9d9d9]",
    {
      "z-10 bg-[#07408e] border-[#07408e] text-white": isActive,
      "hover:bg-[#07408e]! hover:border-[#d9d9d9]! hover:text-white":
        !isActive && position !== "middle",
      //   "text-[#666]": position === "middle",
    }
  );

  return isActive || position === "middle" ? (
    <div className={className}>{page}</div>
  ) : (
    <Link href={href} className={className}>
      {page}
    </Link>
  );
}

function PaginationDirection({
  href,
  direction,
  isDisabled,
  isActive = false,
  children,
}: {
  href: string;
  direction: "left" | "right";
  isDisabled?: boolean;
  isActive?: boolean;
  children: React.ReactNode;
}) {
  const className = clsx(
    "flex h-[26px] px-[9px] m-[2px] items-center text-[14px] text-[#666] justify-center border border-solid border-[#d9d9d9] bg-[#eee]!",
    {
      "pointer-events-none text-gray-300": isDisabled,
      "hover:bg-[#07408e]! hover:border-[#d9d9d9]! hover:text-white": !isActive,
      "mx-[2px]": direction === "left",
      "mx-[px]": direction === "right",
    }
  );

  return isDisabled ? (
    <div className={className}>{children}</div>
  ) : (
    <Link className={className} href={href}>
      {children}
    </Link>
  );
}

function TotalPage({ totalPages }: { totalPages: number }) {
  const className = clsx(
    "flex h-[26px] px-[9px] m-[2px] items-center text-[14px]/[26px] text-[#666] justify-center border border-solid border-[#d9d9d9]"
  );
  return <div className={className}>共&nbsp;{totalPages}&nbsp;页</div>;
}

function JumpTo({ totalPages }: { totalPages: number }) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [page, setPage] = useState("");
  const defaultPage = "1";

  const redirect = () => {
    const params = new URLSearchParams(searchParams);

    const n = Number.parseInt(page.trim());
    if (n > totalPages) {
      message.warning(`页数最大为${totalPages}`, 2);
      setPage(searchParams.get("page")?.toString() || defaultPage);
      return;
    } else if (n < 1) {
      setPage(searchParams.get("page")?.toString() || defaultPage);
      message.warning(`页数最小为1`, 2);
      return;
    } else if (Number.isNaN(n) && page.trim() !== "") {
      setPage(searchParams.get("page")?.toString() || defaultPage);
      message.warning(`请输入1到${totalPages}的数字页数`, 2);
      return;
    }

    params.set("page", page.toString() || defaultPage);
    replace(`${pathname}?${params.toString()}`);
  };

  useEffect(() => {
    if (!searchParams.has("page")) {
      redirect();
    }
  }, []);

  // capturing the user's search input and then need to update the URL with the search term.
  const handleKeyDown = useDebouncedCallback((e) => {
    if (e.key == "Enter") {
      e.target.blur();
      redirect();
    }
  }, 300);

  const className = clsx(
    "flex m-[2px] items-center text-[14px]/[26px] text-[#666] justify-center "
  );

  return (
    <div className={className}>
      <span>跳</span>
      <input
        className="w-[50px] h-[26px] mx-[2px]! px-[5px] py-[5px] border border-solid border-[#d9d9d9] text-center"
        type="text"
        onChange={(e) => setPage(e.target.value)}
        onKeyDown={(e) => handleKeyDown(e)}
        defaultValue={searchParams.get("page")?.toString()}
      />
      <span>/{totalPages}页</span>
    </div>
  );
}
